import React from "react";
import { Redirect } from "react-router-dom";
import { Route } from "react-router-dom";

//import { isUserAuthenticated, getLoggedInUser } from '../helpers/authUtils';
import UserManager from "../core/User";
import { Role, hasPermission } from "../components/Rbac";

// lazy load all the views
const Items = React.lazy(() => import("../pages/items/Items"));
const ItemEditor = React.lazy(() => import("../pages/items/ItemEditor"));
const Contracts = React.lazy(() => import("../pages/contracts/Contracts"));
const ContractDetails = React.lazy(() =>
  import("../pages/contracts/ContractDetail")
);
const Customers = React.lazy(() => import("../pages/customers/Customers"));
const CustomerDetail = React.lazy(() =>
  import("../pages/customers/CustomerDetail")
);
const Vendors = React.lazy(() => import("../pages/vendors/Vendors"));
const VendorDetail = React.lazy(() => import("../pages/vendors/VendorDetail"));
const PrimeContractors = React.lazy(() =>
  import("../pages/prime-contractors/PrimeContractors")
);
const PrimeContractorDetail = React.lazy(() =>
  import("../pages/prime-contractors/PrimeContractorDetail")
);
const Reports = React.lazy(() => import("../pages/reports/Reports"));
const People = React.lazy(() => import("../pages/people/People"));
const Profile = React.lazy(() => import("../pages/people/Profile"));
const Settings = React.lazy(() => import("../pages/settings/Settings"));
const SingleSignOn = React.lazy(() => import("../pages/sso/SingleSignOn"));

// public
const PublicPeopleEmailSettings = React.lazy(() =>
  import("../pages/people-email-settings/PeopleEmailSettings")
);

// auth
const Login = React.lazy(() => import("../pages/auth/Login"));
const Logout = React.lazy(() => import("../pages/auth/Logout"));
const Register = React.lazy(() => import("../pages/auth/Register"));
const ForgetPassword = React.lazy(() => import("../pages/auth/ForgetPassword"));
const ResetPassword = React.lazy(() => import("../pages/auth/ResetPassword"));
const Confirm = React.lazy(() => import("../pages/auth/Confirm"));
const SSOSuccess = React.lazy(() => import("../pages/auth/SSOSuccess"));
// dashboard
const Dashboard = React.lazy(() => import("../pages/dashboard/Dashboard"));

// MFA

const SuperAdmin = React.lazy(() => import("../pages/superadmin/SuperAdmin"));
const Assistant = React.lazy(() => import("../pages/assistant/Assistant"));

/*import Items from '../pages/items/Items';
import ItemEditor from '../pages/items/ItemEditor';
import Contracts from '../pages/contracts/Contracts';
import Customers from '../pages/customers/Customers';
import People from '../pages/people/People';

// auth
import Login from '../pages/auth/Login';
import Logout from '../pages/auth/Logout';
import Register from '../pages/auth/Register';
import ForgetPassword from '../pages/auth/ForgetPassword';
import Confirm from '../pages/auth/Confirm';
// dashboard
import AnalyticsDashboard from '../pages/dashboards/Analytics';
*/

const PrivateRoute = ({ component: Component, roles, minRoles, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      if (!UserManager.isAuthenticated()) {
        // not logged in so redirect to login page with the return url
        return (
          <Redirect
            to={{ pathname: "/account/login", state: { from: props.location } }}
          />
        );
      }

      const userDetails = UserManager.getUserDetailsSync();

      if (
        userDetails &&
        roles &&
        roles.indexOf("kledger_admin") >= 0 &&
        !userDetails.iskledgerAdmin
      ) {
        // role not authorised so redirect to home page
        return <Redirect to={{ pathname: "/" }} />;
      }
      if (minRoles && minRoles.length > 0 && userDetails && userDetails.role) {
        for (let i = 0; i < minRoles.length; i++) {
          const targetRole = minRoles[i];
          if (!hasPermission(userDetails.role, targetRole)) {
            return <Redirect to={{ pathname: "/" }} />;
          }
        }
      }

      //TODO: implement RBAC
      /*const loggedInUser = getLoggedInUser();
            // check if route is restricted by role
            if (roles && roles.indexOf(loggedInUser.role) === -1) {
                // role not authorised so redirect to home page
                return <Redirect to={{ pathname: '/' }} />;
            }*/

      // authorised so return component
      return <Component {...props} />;
    }}
  />
);

// root routes
const rootRoute = {
  path: "/",
  exact: true,
  component: () => <Redirect to="/dashboard" />,
  route: PrivateRoute,
};

const superAdminMenuItem = {
  path: "/super-admin",
  component: SuperAdmin,
  route: PrivateRoute,
  roles: ["kledger_admin"],
};

const assistantMenuItem = {
  path: "/assistant",
  component: Assistant,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

// dashboards
const dashboardMenuItem = {
  path: "/dashboard",
  name: "Dashboard",
  icon: "uil-home-alt",
  header: "Views",
  component: Dashboard,
  route: PrivateRoute,
  minRoles: [Role.Contributor],
};

const itemsMenuItem = {
  path: "/items",
  name: "Items",
  icon: "uil-file-copy-alt",
  component: Items,
  route: PrivateRoute,
  minRoles: [Role.Contributor],
};
const itemsEditorMenuItem = {
  path: "/item-editor",
  exact: true,
  //name: 'Items Editor',
  //icon: 'uil-file-copy-alt',
  component: ItemEditor,
  route: PrivateRoute,
  minRoles: [Role.Contributor],
};

const peopleMenuItem = {
  path: "/people",
  name: "People",
  icon: "dripicons-user-group",
  component: People,
  header: "Administrative",
  route: PrivateRoute,
  minRoles: [Role.Admin],
};
const profileMenuItem = {
  path: "/profile",
  component: Profile,
  route: PrivateRoute,
  minRoles: [Role.Contributor],
};

const contractsMenuItem = {
  path: "/contracts",
  name: "Contracts",
  icon: "uil-file-check-alt",
  component: Contracts,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};
const contractDetailMenuItem = {
  path: "/contract",
  component: ContractDetails,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const customersMenuItem = {
  path: "/customers",
  name: "Customers",
  icon: "uil-book-reader",
  component: Customers,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const customerDetailMenuItem = {
  path: "/customer",
  component: CustomerDetail,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const vendorMenuItem = {
  path: "/vendors",
  name: "Vendors",
  icon: "uil-flip-h",
  component: Vendors,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const vendorDetailMenuItem = {
  path: "/vendor",
  component: VendorDetail,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const reportDetailMenuItem = {
  path: "/reports",
  name: "Reports",
  icon: "uil-graph-bar",
  component: Reports,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const primeContractorsMenuItem = {
  path: "/prime-contractors",
  name: "Prime Contractors",
  icon: "uil-user-square",
  component: PrimeContractors,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const primeContractorDetailMenuItem = {
  path: "/prime-contractor",
  component: PrimeContractorDetail,
  route: PrivateRoute,
  minRoles: [Role.Admin],
};

const settingsMenuItem = {
  path: "/settings",
  name: "Settings",
  icon: "uil-bright",
  header: "Other",
  component: Settings,
  route: PrivateRoute,
  minRoles: [Role.Contributor],
};

const ssoConfigMenuItem = {
  path: "/sso",
  name: "Single sign-on",
  icon: "uil-unlock-alt",
  component: SingleSignOn,
  route: PrivateRoute,
  minRoles: [Role.SuperAdmin],
};

// Email settings people
const peopleEmailSettingsMenuItem = {
  path: "/public/email-settings",
  name: "Email Settings",
  component: PublicPeopleEmailSettings,
  route: Route,
};

// auth
const authRoutes = {
  path: "/account",
  name: "Auth",
  children: [
    {
      path: "/account/login",
      name: "Login",
      component: Login,
      route: Route,
    },
    {
      path: "/account/logout",
      name: "Logout",
      component: Logout,
      route: Route,
    },
    {
      path: "/account/register",
      name: "Register",
      component: Register,
      route: Route,
    },
    {
      path: "/account/confirm",
      name: "Confirm",
      component: Confirm,
      route: Route,
    },
    {
      path: "/auth/sso-success",
      name: "SSOSuccess",
      component: SSOSuccess,
      route: Route,
    },
    {
      path: "/account/forget-password",
      name: "Forget Password",
      component: ForgetPassword,
      route: Route,
    },
    {
      path: "/account/reset-password",
      name: "Reset Password",
      component: ResetPassword,
      route: Route,
    },
  ],
};

// flatten the list of all nested routes
const flattenRoutes = (routes) => {
  let flatRoutes = [];

  routes = routes || [];
  routes.forEach((item) => {
    flatRoutes.push(item);

    if (typeof item.children !== "undefined") {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

// All routes
const allRoutes = [
  rootRoute,
  superAdminMenuItem,
  assistantMenuItem,
  //mfaItem,
  dashboardMenuItem,
  itemsMenuItem,
  itemsEditorMenuItem,
  peopleMenuItem,
  profileMenuItem,
  contractsMenuItem,
  contractDetailMenuItem,
  customersMenuItem,
  customerDetailMenuItem,
  vendorMenuItem,
  vendorDetailMenuItem,
  reportDetailMenuItem,
  //primeContractorsMenuItem,
  //primeContractorDetailMenuItem,
  settingsMenuItem,
  ssoConfigMenuItem,
  // dashboardRoutes,
  // ...appRoutes,
  authRoutes,
  peopleEmailSettingsMenuItem,
  //pageRoutes,
  //uiRoutes,
];

const authProtectedRoutes = [
  superAdminMenuItem,
  assistantMenuItem,
  //mfaItem,
  dashboardMenuItem,
  itemsMenuItem,
  //itemsEditorMenuItem,
  contractsMenuItem,
  customersMenuItem,
  vendorMenuItem,
  //primeContractorsMenuItem,
  //settingsMenuItem,
  peopleMenuItem,
  reportDetailMenuItem,
  ssoConfigMenuItem,
  //dashboardRoutes,
  //...appRoutes,
  //pageRoutes,
  //uiRoutes,
];

const allFlattenRoutes = flattenRoutes(allRoutes);

export { allRoutes, authProtectedRoutes, allFlattenRoutes };
